import React, { useEffect, useState, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from './User';
import { seedCurrency } from '../core/currency';

export const SettingsContext = createContext();

// Helper function to get query parameter from URL
const queryParam = (name) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

const SettingsContextProvider = (props) => {
  const { user } = useContext(UserContext);
  const [settings, setSettings] = useState({});
  const location = useLocation();
  const [merchantUuid] = useState(queryParam('merchant') || null);
  const [shopifyToken] = useState(queryParam('shopify') || null);
  // 2026-04-21 finding F6
  const [retailCircleToken] = useState(queryParam('retail_circle') || null);
  const [anonClientContext] = useState(queryParam('anon_client_context') === 'yes')
  const [isShopify] = useState(
    !!(user?.merchant_id || location?.search.indexOf('shopify=') > -1)
    && location?.search.indexOf('retail_circle=') === -1
  );
  const [isMerchant] = useState(!!(user?.merchant_id || location?.search.indexOf('merchant=') > -1));
  const [isRetailCircle] = useState(
    location?.search.indexOf('retail_circle=') > -1 ||
    window.location.hostname.includes('retail-circle') ||
    window.location.hostname.includes('retail-circle.io') ||
    (import.meta.env.VITE_API_URL || '').includes('retail-circle')
  );
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [context, setContext] = useState({ settings, settingsLoaded: false, isShopify, isMerchant, isRetailCircle });

  const loadSettings = async () => {
    try {
      const { data } = await axios.get('/api/app-settings', { metadata: { silent: true } });

      // Multi-currency: the payload carries base_currency + currencies only
      // while the backend flag is on; seedCurrency is a no-op otherwise and
      // the app stays in legacy £ mode (see src/core/currency.js).
      seedCurrency(data?.base_currency, data?.currencies);

      setSettings(data);
    } finally {
      // Mark loaded even when a store has no settings yet (empty {}) — otherwise
      // consumers that gate on "settings present" spin forever for a fresh tenant.
      setSettingsLoaded(true);
    }
  }

  useEffect(() => {
    loadSettings();
  }, [user?.id]);

  useEffect(() => {
    const shopifyParam = shopifyToken ? `shopify=${shopifyToken}` : '';
    const merchantParam = merchantUuid ? `merchant=${merchantUuid}` : '';
    const retailCircleParam = retailCircleToken ? `retail_circle=${retailCircleToken}` : '';
    const anonClientContextParam = anonClientContext ? 'anon_client_context=yes' : '';

    setContext({
      settings,
      settingsLoaded,
      isShopify,
      isRetailCircle,
      retailCircleToken,
      isMerchant,
      shopifyToken,
      merchantUuid,
      // Lets settings consumers refetch after a change (e.g. base currency)
      // so the seeded currency + the rest of the UI update without a reload.
      reloadSettings: loadSettings,
      iframeQueryParams: [shopifyParam, merchantParam, retailCircleParam, anonClientContextParam].filter(p => p).join('&'),
    });
  }, [
    settings,
    settingsLoaded,
    isShopify,
    isRetailCircle,
    retailCircleToken,
    isMerchant,
    shopifyToken,
    merchantUuid,
  ]);

  if (isShopify && isRetailCircle) {
    return (
      <div style={{ padding: '100px' }}>
        <h1>Invalid configuration</h1>
      </div>
    );
  }

  return (
    <SettingsContext.Provider value={context}>
      {props.children}
    </SettingsContext.Provider>
  )
}

export default SettingsContextProvider
